import { haptic } from '../haptic.js';
import { Calendar, Pause, Play } from '../icons.js';
import { useMenu } from '../menu.js';

import { SprintMenu } from './SprintMenu.js';

import type { TaskExtras, TaskHandlers } from './TaskParts.js';
import type { Task } from '@nedelnik/shared';

/**
 * Действия в виджете задачи: строка списка и карточка доски (3.1.7).
 *
 * Здесь по очереди стояли два степпера со стрелками, потом три названные
 * кнопки во всю ширину — и обе версии решали одну задачу за счёт другой. Со
 * стрелками приходилось догадываться, что будет дальше; с тремя кнопками
 * догадываться стало не нужно, но управление занимало целую строку на каждой
 * задаче, и на экран телефона помещалось вдвое меньше дел.
 *
 * Итог: **в виджете остаётся одно действие — самое частое**. «Взять в работу»
 * и «вернуть в бэклог» стоят значком в строке заголовка, справа, рядом с
 * отметкой «готово». Значок без подписи здесь допустим (принцип 3): «play» и
 * «pause» читаются однозначно и подписаны для озвучки.
 *
 * Переносы между спринтами и во Входящие живут в карточке задачи (3.1.7). Они
 * редкие — их делают на планировании, — а собственная кнопка в виджете стоила
 * целой строки у каждой задачи без меток: одна строка на задачу это минус одна
 * задача на экране телефона.
 *
 * Исключение — сами Входящие (3.4.6). Там задача ещё не назначена ни на какую
 * неделю, и частый вопрос другой: не «делаю ли я это сейчас», а «когда». «В
 * работу» во Входящих почти не нужно — задача без недели в работе выглядит
 * странно, — поэтому её место занимает выбор недели.
 *
 * Меню там открывается то же самое, что в карточке (`SprintMenu`, 3.5.6): один
 * вопрос — один ответ, и неважно, откуда его задали.
 */

/** Кнопка «в работу» / «в бэклог»: одно движение, самое частое (3.1.7). */
export function RowAction({
  task,
  extras,
  handlers,
}: {
  task: Task;
  extras: TaskExtras;
  handlers: TaskHandlers;
}): React.ReactElement | null {
  // У закрытой задачи двигать нечего (3.1.8): если решение переиграно, снимают
  // отметку — и задача возвращается в бэклог тем же действием, каким её
  // закрыли.
  if (task.status === 'done') return null;

  // Задача без недели бывает только во Входящих: на экране спринта их
  // отбирают по самому спринту. Признак берётся у задачи, а не у экрана —
  // тогда правило верно и на доске, и в поиске по всем спринтам.
  if (task.sprint === null) {
    return <PlanMenu task={task} extras={extras} handlers={handlers} />;
  }

  const toWip = task.status === 'backlog';

  return (
    <button
      type="button"
      className={`iconbtn act act--${toWip ? 'start' : 'stop'}`}
      aria-label={toWip ? 'Взять в работу' : 'Вернуть в бэклог'}
      title={toWip ? 'Взять в работу' : 'Вернуть в бэклог'}
      onClick={() => {
        haptic();
        handlers.onShiftStatus(task, toWip ? 1 : -1);
      }}
    >
      {toWip ? <Play /> : <Pause />}
    </button>
  );
}

/**
 * Выбор недели для задачи из Входящих (3.4.6).
 *
 * Меню, а не ряд кнопок: рядом с названием помещается только подпись вроде
 * «н36», а по ней непонятно, какие это дни. В строке меню умещается и то, и
 * другое — «Неделя 37 · 7–13 сентября», — и выбирать можно по датам, как
 * человек и планирует.
 *
 * Содержимое меню общее с карточкой (3.5.6). Своих подписей — «через спринт» —
 * здесь больше нет: они называли период относительно показанного, а тот же
 * период в карточке назывался номером недели, и два экрана говорили о нём
 * по-разному.
 */
export function PlanMenu({
  task,
  extras,
  handlers,
  wide = false,
}: {
  task: Task;
  extras: TaskExtras;
  handlers: TaskHandlers;
  /**
   * Кнопка словом «Запланировать» вместо значка — на доске с мышью, где она
   * стоит во всю ширину под отметками (3.1.10). Значок в строке названия там
   * висел рядом с невидимой галочкой.
   */
  wide?: boolean;
}): React.ReactElement {
  // Меню кнопки на карточке доски закрывается, стоит мыши уйти с карточки:
  // кнопка без наведения прячется, и раскрытый список без неё висел бы сам.
  const menu = useMenu({ closeOnLeave: wide ? '.card' : undefined });
  const { toggle } = menu;

  return (
    <span className="menu" ref={menu.box}>
      {wide ? (
        <button
          type="button"
          className="planbtn"
          aria-haspopup="menu"
          aria-expanded={menu.open}
          onClick={toggle}
        >
          <Calendar />
          Запланировать
        </button>
      ) : (
        <button
          type="button"
          className="iconbtn act act--plan"
          aria-label="Выбрать спринт"
          aria-haspopup="menu"
          aria-expanded={menu.open}
          title="Выбрать спринт"
          onClick={toggle}
        >
          <Calendar />
        </button>
      )}

      {menu.open && (
        <SprintMenu
          task={task}
          current={extras.shownSprint}
          label="В какой спринт"
          onPick={(sprint) => {
            handlers.onPlan(task, sprint);
            menu.close();
          }}
        />
      )}
    </span>
  );
}
